import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sun, Moon, ArrowRight, ShieldCheck, User, Lock, AlertCircle, Eye, EyeOff } from "lucide-react";
import { cn } from "../lib/utils";
import { PreviewFooter } from "../components/preview/PreviewFooter";
import { PAID } from "../config/preview";

const ROLES = [
  { id: "admin", label: "Admin", hint: "Full access · manage properties & settings" },
  { id: "client", label: "Client", hint: "Read-only dashboard · view compliance status" },
];

export default function Login() {
  const { user, login } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const [role, setRole] = useState("admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = (e) => {
    e.preventDefault();
    setError("");
    const result = login({ email, password, role });
    if (!result.ok) {
      setError(result.error || "Sign in failed");
      return;
    }
    navigate("/dashboard");
  };

  const onRoleChange = (id) => {
    setRole(id);
    setEmail("");
    setPassword("");
    setError("");
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-100 dark:bg-slate-950 px-4 py-12 relative">
      {/* Background grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.35] dark:opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15,23,42,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.06) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      {/* Theme toggle */}
      <button
        data-testid="login-theme-toggle"
        onClick={toggle}
        aria-label="Toggle theme"
        className="absolute top-6 right-6 h-9 w-9 flex items-center justify-center rounded-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 z-10"
      >
        {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
      </button>

      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="h-10 w-10 rounded-sm bg-slate-900 dark:bg-slate-100 flex items-center justify-center">
            <span className="font-display text-white dark:text-slate-900 font-bold">F</span>
          </div>
          <div className="leading-tight">
            <p className="font-display font-semibold tracking-tight text-lg text-slate-900 dark:text-slate-100">
              Factor
            </p>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Operations
            </p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm">
          {/* Role tabs */}
          <div
            role="tablist"
            aria-label="Sign in as"
            data-testid="login-role-tabs"
            className="grid grid-cols-2 border-b border-slate-200 dark:border-slate-800"
          >
            {ROLES.map((r) => (
              <button
                key={r.id}
                role="tab"
                aria-selected={role === r.id}
                data-testid={`login-role-${r.id}`}
                onClick={() => onRoleChange(r.id)}
                className={cn(
                  "px-4 py-3.5 text-sm font-medium transition-colors relative",
                  role === r.id
                    ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-900"
                    : "text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 hover:text-slate-800 dark:hover:text-slate-200"
                )}
              >
                {r.label}
                {role === r.id && (
                  <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-slate-900 dark:bg-slate-100" />
                )}
              </button>
            ))}
          </div>

          <div className="px-6 py-6">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Sign in as {role}
            </p>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 mt-1">
              {role === "admin" ? "Admin portal" : "Client portal"}
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {ROLES.find((r) => r.id === role).hint}
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-500">
                  Email
                </Label>
                <div className="mt-1.5 relative">
                  <User
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={`${role}@rockstarsdigitals.com`}
                    autoComplete="email"
                    className="pl-9"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-500">
                  Password
                </Label>
                <div className="mt-1.5 relative">
                  <Lock
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="pl-9 pr-10"
                  />
                  <button
                    type="button"
                    data-testid="login-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  className="flex items-start gap-2 rounded-sm border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 px-3 py-2 text-xs text-rose-700 dark:text-rose-300"
                >
                  <AlertCircle size={13} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                data-testid="login-submit-btn"
                className="w-full h-11 gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white text-white rounded-sm"
              >
                Enter {role === "admin" ? "admin" : "client"} portal <ArrowRight size={15} />
              </Button>
            </form>
          </div>

          {/* Footer note */}
          <div className="border-t border-slate-200 dark:border-slate-800 px-6 py-3 bg-slate-50 dark:bg-slate-800/40">
            <p className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              <ShieldCheck size={12} /> Prototype build · Access is restricted to authorised users
            </p>
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400 dark:text-slate-500 mt-6">
          Factor Operations · Compliance dashboard prototype
        </p>
        {!PAID && <PreviewFooter />}
      </div>
    </div>
  );
}
